// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
};

// App Configuration
export const APP_CONFIG = {
  APP_NAME: 'UniiClub',
  VERSION: '1.0.0',
  DEFAULT_PAGE_SIZE: 20,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  SUPPORTED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
};

// Branches available for registration
export const BRANCHES = [
  'Computer Science',
  'Information Technology',
  'Electronics and Communication',
  'Mechanical Engineering',
  'Civil Engineering',
  'Electrical Engineering',
  'Chemical Engineering',
  'Biomedical Engineering',
  'Aerospace Engineering',
  'Mathematics',
  'Physics',
  'Chemistry',
  'Biology',
  'Business Administration',
  'Economics',
  'Psychology',
  'English Literature',
  'Political Science',
  'Sociology',
  'History',
];

// User roles
export const USER_ROLES = {
  STUDENT: 'Student' as const,
  FACULTY: 'Faculty' as const,
};

// Club member roles
export const CLUB_ROLES = {
  PRESIDENT: 'President',
  VICE_PRESIDENT: 'Vice President',
  SECRETARY: 'Secretary',
  TREASURER: 'Treasurer',
  MEMBER: 'Member',
};

// Activity types
export const ACTIVITY_TYPES = {
  ANNOUNCEMENT: 'announcement',
  ACTIVITY: 'activity',
  REQUIREMENT: 'requirement',
  EVENT: 'event',
  DISCUSSION: 'discussion',
} as const;

// Priority levels
export const PRIORITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

// Discussion categories
export const DISCUSSION_CATEGORIES = {
  GENERAL: 'general',
  TECHNICAL: 'technical',
  EVENTS: 'events',
  SUPPORT: 'support',
} as const;

// Application statuses
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'unii_access_token',
  REFRESH_TOKEN: 'unii_refresh_token',
  USER_DATA: 'unii_user_data',
  THEME_MODE: 'unii_theme_mode',
} as const;

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  PENDING_APPROVAL: '/pending-approval',

  // Main app
  DASHBOARD: '/dashboard',
  CLUBS: '/clubs',
  FEED: '/feed',
  PROFILE: '/profile',

  // Club routes
  CLUB_DETAILS: '/clubs/:clubId',
  CLUB_INSIDER: '/clubs/:clubId/insider',
  CLUB_QUIZ: '/clubs/:clubId/quiz',
  CLUB_APPLICATION_PENDING: '/clubs/:clubId/application-pending',
  CLUB_MANAGEMENT: '/clubs/:clubId/management',
  CLUB_DISCUSSIONS: '/clubs/:clubId/discussions',
  CREATE_CLUB: '/create-club',

  // Post routes
  CREATE_POST: '/create-post',
  POST_COMMENTS: '/posts/:postId/comments',
  MY_POSTS: '/my-posts',

  // Profile routes
  VIEW_PROFILE: '/profile/:userId',
  EDIT_PROFILE: '/profile/edit',
  STUDENT_APPROVAL: '/student-approval',

  // Application routes
  MY_APPLICATIONS: '/my-applications',

  // Discussion routes
  DISCUSSION_THREAD: '/clubs/:clubId/discussions/:discussionId',
  CREATE_DISCUSSION: '/clubs/:clubId/discussions/create',

  // Activity routes
  ACTIVITY_COMMENTS: '/clubs/:clubId/activities/:activityId/comments',
} as const;

// Validation rules
export const VALIDATION_RULES = {
  PASSWORD_MIN_LENGTH: 6,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  COLLEGE_EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.edu$/,
  POST_MAX_LENGTH: 2000,
  COMMENT_MAX_LENGTH: 500,
  CLUB_NAME_MAX_LENGTH: 100,
  CLUB_DESCRIPTION_MAX_LENGTH: 1000,
  QUIZ_QUESTION_MAX_LENGTH: 500,
  QUIZ_OPTION_MAX_LENGTH: 200,
} as const;

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied. Insufficient permissions.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Internal server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  FILE_TOO_LARGE: `File size must be less than ${APP_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB`,
  UNSUPPORTED_FILE_TYPE: 'File type not supported. Please upload an image file.',
  LOGIN_FAILED: 'Invalid email or password.',
  REGISTRATION_FAILED: 'Registration failed. Please try again.',
  EMAIL_ALREADY_EXISTS: 'An account with this email already exists.',
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Welcome back!',
  REGISTRATION_SUCCESS: 'Registration successful! Please wait for approval.',
  POST_CREATED: 'Post created successfully!',
  POST_LIKED: 'Post liked!',
  POST_UNLIKED: 'Post unliked!',
  COMMENT_ADDED: 'Comment added successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  CLUB_CREATED: 'Club created successfully!',
  APPLICATION_SUBMITTED: 'Application submitted successfully!',
  STUDENT_APPROVED: 'Student approved successfully!',
  BADGE_AWARDED: 'Badge awarded successfully!',
} as const;